import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { getFileExtension, getFileNameWithoutExtension } from './lib';
import { saveFile } from './indexedDb';
import { resizeImage, createFileFromBlob } from './imageHelper';

/**
 * Helper para manejar la subida de archivos al backend
 * 
 * @param {Object} options - Opciones de configuración
 * @param {File[]} options.files - Array de archivos a subir
 * @param {string} options.tableName - Nombre de la tabla en la base de datos
 * @param {string} options.type - Tipo de archivo (PRIVATE, PUBLIC, etc.)
 * @param {Function} [options.onProgress] - Callback para progreso de subida
 * @param {Function} [options.onFileStart] - Callback cuando inicia la subida de un archivo
 * @param {Function} [options.onFileComplete] - Callback cuando completa la subida de un archivo
 * @param {Function} [options.onError] - Callback para errores
 * @param {number} [options.maxFileSize] - Tamaño máximo de archivo en bytes (default: 5MB)
 * 
 * @returns {Promise<Array>} Array de objetos con información de los archivos subidos
 */
export const uploadFiles = async ({
  files,
  tableName,
  type = 'PRIVATE',
  onProgress,
  onFileStart,
  onFileComplete,
  onError,
  maxFileSize = 5 * 1024 * 1024 // 5MB por defecto
}) => {
  if (!files || !Array.isArray(files) || files.length === 0) {
    throw new Error('Se requiere un array de archivos válido');
  }

  if (!tableName) {
    throw new Error('Se requiere el nombre de la tabla');
  }

  const uploadedFiles = [];
  const errors = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    try {
      // Notificar inicio de archivo
      if (onFileStart) {
        onFileStart(file, i, files.length);
      }

      // Validar tamaño del archivo
      if (file.size > maxFileSize) {
        const error = `El archivo ${file.name} no puede superar los ${Math.round(maxFileSize / (1024 * 1024))}MB`;
        errors.push({ file: file.name, error });
        if (onError) {
          onError(error, file);
        }
        continue;
      }

      // Usar ID único existente o generar uno nuevo
      const unique_id = file.unique_id || uuidv4();
      
      // Obtener extensión del archivo
      const extension = getFileExtension(file.name);
      const file_name = unique_id + '.' + extension;

      //console.log('uploading: ', unique_id, file_name);

      // Paso 1: Preparar la subida
      const prepareResponse = await axios.post(
        `${import.meta.env.VITE_API_URL}s3_upload_prepare`,
        {
          unique_id: unique_id,
          table_name: tableName,
          file_name: file_name,
          type: type
        },
        { withCredentials: true }
      );

      if (prepareResponse.data.data !== unique_id) {
        throw new Error('Error en la preparación de la subida');
      }

      // Paso 2: Subir el archivo
      const formData = new FormData();
      formData.append('file', file);
      formData.append('context', JSON.stringify({
        unique_id: unique_id,
      }));

      const uploadResponse = await axios.post(
        `${import.meta.env.VITE_API_URL}s3_upload`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            if (onProgress) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onProgress(percentCompleted, file, i, files.length);
            }
          }
        }
      );

      if (uploadResponse.data.data !== 1) {
        throw new Error('Error en la subida del archivo');
      }

      // Paso 3: Guardar en IndexedDB
      await saveFile(file_name, file);

      // Crear objeto de archivo subido
      const uploadedFile = {
        name: file.name,
        type: file.type,
        size: file.size,
        url: URL.createObjectURL(file),
        unique_id: unique_id,
        file_name: file_name,
        table_name: tableName,
        upload_type: type
      };

      uploadedFiles.push(uploadedFile);

      // Notificar completación del archivo
      if (onFileComplete) {
        onFileComplete(uploadedFile, i, files.length);
      }

    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error desconocido';
      errors.push({ file: file.name, error: errorMessage });
      
      if (onError) {
        onError(errorMessage, file);
      }
    }
  }

  // Retornar resultado
  const result = {
    uploadedFiles,
    errors,
    success: errors.length === 0,
    totalFiles: files.length,
    successfulUploads: uploadedFiles.length,
    failedUploads: errors.length
  };

  return result;
};

/**
 * Versión simplificada para subir un solo archivo
 * 
 * @param {Object} options - Opciones de configuración
 * @param {File} options.file - Archivo a subir
 * @param {string} options.tableName - Nombre de la tabla
 * @param {string} options.type - Tipo de archivo
 * @param {Function} [options.onProgress] - Callback para progreso
 * @param {Function} [options.onError] - Callback para errores
 * @param {number} [options.maxFileSize] - Tamaño máximo del archivo
 * 
 * @returns {Promise<Object>} Información del archivo subido
 */
export const uploadSingleFile = async (options) => {
  const { file, ...restOptions } = options;
  
  if (!file) {
    throw new Error('Se requiere un archivo válido');
  }

  const result = await uploadFiles({
    files: [file],
    ...restOptions
  });

  if (!result.success) {
    throw new Error(result.errors[0]?.error || 'Error al subir el archivo');
  }

  return result.uploadedFiles[0];
};

/**
 * Función para marcar archivos como eliminados (soft delete)
 * 
 * @param {Array} attachedFiles - Array de archivos adjuntos
 * @param {string} uniqueId - ID único del archivo a eliminar
 * 
 * @returns {Array} Array actualizado con el archivo marcado como eliminado
 */
export const markFileAsDeleted = (attachedFiles, uniqueId) => {
  return attachedFiles.map(file => 
    file.unique_id === uniqueId 
      ? { ...file, deleted: 1 }
      : file
  );
};

/**
 * Función para filtrar archivos no eliminados
 * 
 * @param {Array} attachedFiles - Array de archivos adjuntos
 * 
 * @returns {Array} Array filtrado sin archivos eliminados
 */
export const getActiveFiles = (attachedFiles) => {
  return attachedFiles.filter(file => file.deleted !== 1);
};

/**
 * Función para preparar archivos para envío al backend
 * 
 * @param {Array} attachedFiles - Array de archivos adjuntos
 * 
 * @returns {Array} Array con solo unique_id y deleted para el backend
 */
export const prepareFilesForBackend = (attachedFiles) => {
  return attachedFiles.map(file => ({
    unique_id: file.unique_id,
    deleted: file.deleted || 0
  }));
};

/**
 * Función de utilidad para validar tipos de archivo
 * 
 * @param {File} file - Archivo a validar
 * @param {Array} allowedTypes - Array de tipos MIME permitidos
 * 
 * @returns {boolean} True si el tipo es válido
 */
export const validateFileType = (file, allowedTypes = []) => {
  if (allowedTypes.length === 0) return true;
  return allowedTypes.includes(file.type);
};

/**
 * Función de utilidad para obtener un mensaje de progreso amigable
 * 
 * @param {number} currentFile - Índice del archivo actual
 * @param {number} totalFiles - Total de archivos
 * @param {number} progress - Progreso del archivo actual (0-100)
 * 
 * @returns {string} Mensaje de progreso
 */
export const getProgressMessage = (currentFile, totalFiles, progress) => {
  if (totalFiles === 1) {
    return `Subiendo archivo... ${progress}%`;
  }
  return `Subiendo archivo ${currentFile + 1} de ${totalFiles}... ${progress}%`;
};

/**
 * Función reutilizable para subir una imagen original y su thumbnail
 * 
 * @param {Object} options - Opciones de configuración
 * @param {File} options.originalFile - Archivo de imagen original
 * @param {string} options.tableName - Nombre de la tabla ('owners', 'pets', etc.)
 * @param {Object} [options.thumbnailOptions] - Opciones para el thumbnail
 * @param {number} [options.thumbnailOptions.width=128] - Ancho del thumbnail
 * @param {number} [options.thumbnailOptions.height=128] - Alto del thumbnail
 * @param {number} [options.thumbnailOptions.quality=0.8] - Calidad del thumbnail (0-1)
 * @param {string} [options.thumbnailOptions.suffix='_thumb'] - Sufijo para el nombre del thumbnail
 * @param {Object} [options.fileSizeLimits] - Límites de tamaño de archivo
 * @param {number} [options.fileSizeLimits.original=5*1024*1024] - Límite para imagen original (bytes)
 * @param {number} [options.fileSizeLimits.thumbnail=1*1024*1024] - Límite para thumbnail (bytes)
 * @param {string} [options.type='PUBLIC'] - Tipo de archivo para el upload
 * @param {Function} [options.onProgress] - Callback para progreso de subida
 * @param {Function} [options.onError] - Callback para errores
 * 
 * @returns {Promise<Object>} Objeto con las imágenes subidas { original, thumbnail }
 */
export const uploadPhotoAndThumbnail = async ({
  originalFile,
  tableName,
  thumbnailOptions = {},
  fileSizeLimits = {},
  type = 'PUBLIC',
  onProgress,
  onError
}) => {
  if (!originalFile) {
    throw new Error('Se requiere un archivo de imagen válido');
  }
  
  if (!tableName) {
    throw new Error('Se requiere el nombre de la tabla');
  }

  // Configuración por defecto para thumbnail
  const {
    width = 128,
    height = 128,
    quality = 0.8,
    suffix = '_thumb'
  } = thumbnailOptions;

  // Configuración por defecto para límites de tamaño
  const {
    original: originalMaxSize = 5 * 1024 * 1024, // 5MB
    thumbnail: thumbnailMaxSize = 1 * 1024 * 1024 // 1MB
  } = fileSizeLimits;

  try {
    // Generar unique_id para ambas imágenes
    const unique_id = getFileNameWithoutExtension(originalFile.name);
    
    // Crear versión thumbnail
    const thumbnailBlob = await resizeImage(originalFile, width, height, quality);
    const thumbnailFile = createFileFromBlob(thumbnailBlob, `${unique_id}${suffix}.jpg`);
    
    // Asignar unique_id a ambos archivos
    originalFile.unique_id = unique_id;
    thumbnailFile.unique_id = `${unique_id}${suffix}`;
    
    // Subir imagen original
    const originalUpload = await uploadSingleFile({
      file: originalFile,
      tableName,
      type,
      maxFileSize: originalMaxSize,
      onProgress: (progress) => {
        if (onProgress) {
          onProgress(progress, 'original', originalFile);
        }
      },
      onError
    });
    
    // Subir imagen thumbnail
    const thumbnailUpload = await uploadSingleFile({
      file: thumbnailFile,
      tableName,
      type,
      maxFileSize: thumbnailMaxSize,
      onProgress: (progress) => {
        if (onProgress) {
          onProgress(progress, 'thumbnail', thumbnailFile);
        }
      },
      onError
    });
    
    return {
      original: originalUpload,
      thumbnail: thumbnailUpload
    };
    
  } catch (error) {
    if (onError) {
      onError(error.message || 'Error al subir las imágenes', originalFile);
    }
    throw error;
  }
};
